import type { SportsPayOnboarding } from './types'

/**
 * Validates a server-supplied `app_link` before it is used as an iframe `src`
 * or an anchor `href`.
 *
 * This is a security boundary, not a formatting nicety: a `javascript:` URL in
 * either position executes in our origin the moment the user clicks. Only
 * `https:` is allowed through — SportsPay serves the hosted application over
 * TLS, so anything else is either a mistake or an attack.
 *
 * Returns the trimmed URL, or `undefined` when it is unusable. Callers should
 * hide the Open control entirely when this returns `undefined`.
 */
export const resolveSportsPayAppLink = (
  appLink: string | null | undefined
): string | undefined => {
  const trimmed = appLink?.trim()
  if (!trimmed) {
    return undefined
  }

  try {
    // `URL` throws on anything unparseable, which covers the bare-text case.
    const parsed = new URL(trimmed)
    // `protocol` is normalized to lowercase by the parser, so a case-varied
    // `JavaScript:` is caught here too.
    return parsed.protocol === 'https:' ? trimmed : undefined
  } catch {
    return undefined
  }
}

/**
 * Turns SportsPay's PascalCase state into display text ('UnderReview' → 'Under
 * Review').
 *
 * Derived rather than looked up on purpose: the full set of states belongs to
 * SportsPay, and a lookup table would render an unknown state as blank. This
 * degrades to the raw value instead, which is always readable.
 */
export const formatSportsPayState = (
  state: string | null | undefined
): string => {
  const trimmed = state?.trim()
  if (!trimmed) {
    return '—'
  }
  return trimmed.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
}

/**
 * States where the hosted application still has something for the merchant to
 * do. Anything past these is either with SportsPay (Submitted, UnderReview) or
 * finished, so opening the link would land on a page with nothing actionable.
 */
const OPENABLE_STATES = new Set(['created', 'opened', 'inprogress'])

/**
 * Whether to offer the Open button for an application.
 *
 * Two conditions: the state must still be in progress, and there must be a
 * usable https link (`resolveSportsPayAppLink`).
 *
 * Unknown states are NOT openable — the opposite default to
 * `shouldShowSportsPaySignupButton`. Hiding that button would strand the org
 * with no way to apply at all, whereas here the status is still displayed and
 * only a link we cannot vouch for is withheld.
 */
export const canOpenSportsPayApplication = (
  application: SportsPayOnboarding | null | undefined
): boolean => {
  if (!application) {
    return false
  }
  if (!resolveSportsPayAppLink(application.app_link)) {
    return false
  }
  const state = application.state?.trim().toLowerCase()
  return Boolean(state && OPENABLE_STATES.has(state))
}

/**
 * Picks which application the modal should display.
 *
 * The FETCHED value wins whenever there is one: it is the only copy that
 * refreshes, so preferring the locally created snapshot would mask every
 * refetch — including the ones fired when the embedded frame closes and when
 * the tab regains focus, which exist precisely to surface SportsPay-side
 * progress.
 *
 * The locally created application still matters for the moment right after
 * submitting: the GET has already cached `null` (that is why the form was
 * showing), so it fills the gap until a refetch lands.
 */
export const resolveActiveSportsPayApplication = (
  fetched: SportsPayOnboarding | null | undefined,
  created: SportsPayOnboarding | null | undefined
): SportsPayOnboarding | null => fetched ?? created ?? null

/** Which body the signup modal should render. */
export type SportsPayView = 'loading' | 'status' | 'form'

export interface ResolveSportsPayViewArgs {
  isLoading: boolean
  application: SportsPayOnboarding | null | undefined
}

/**
 * Picks the modal's body from the onboarding fetch.
 *
 * An existing application replaces the form outright, so a second one cannot be
 * filed by accident. A failed fetch arrives here as `null` — deliberately
 * indistinguishable from "none yet" — which falls through to the form: the user
 * can still apply during a backend blip, and the POST answers 200 with
 * `created: false` if one already existed.
 */
export const resolveSportsPayView = ({
  isLoading,
  application,
}: ResolveSportsPayViewArgs): SportsPayView => {
  if (isLoading) {
    return 'loading'
  }
  // `app_id` is what makes a response an application; an empty object is not.
  return application?.app_id ? 'status' : 'form'
}

/**
 * The SportsPay block the organization payload carries
 * (`GET /organizations/{id}` → `data.sportspay`).
 */
export interface OrganizationSportsPay {
  application_state?: string | null
  application_url?: string | null
  terminal_id?: string | null
  is_onboarded?: boolean
}

/** The slice of the organization payload this gate reads. */
export interface SportsPayEligibleOrganization {
  is_affiliate?: boolean
  sportspay?: OrganizationSportsPay | null
  /**
   * The account this org belongs to. Needed because the completion PUT is
   * ACCOUNT-scoped while the rest of this feature is org-scoped — it is also
   * what SportsPay receives as `OrgID` in the registration URL.
   */
  account_id?: string
}

/**
 * The viewer's permissions on the organization, from the org response's
 * `meta.permissions[orgId]`.
 */
export interface SportsPayViewerPermission {
  admin?: boolean
}
/**
 * What the page header should do with the SportsPay signup button.
 *
 * - `active`   — offer it; the org can start a registration.
 * - `disabled` — show it, unclickable, reading "in progress": SportsPay has
 *   issued a terminal but the account is not live yet.
 * - `hidden`   — do not render it at all.
 */
export type SportsPaySignupButtonState = 'active' | 'disabled' | 'hidden'

/**
 * Decides the signup button's state from the organization payload, which
 * already carries the SportsPay block — no extra request.
 *
 * Hidden unless the viewer is an admin and the org is an affiliate. Signing up
 * for a payment provider is an account-level commitment, so it is gated on
 * `admin` specifically rather than `update` — the same bar the org page uses
 * for its other administrative actions.
 *
 * Beyond that, `terminal_id` and `is_onboarded` together describe how far the
 * registration has got:
 *
 * | terminal_id | is_onboarded | state    |
 * | ----------- | ------------ | -------- |
 * | absent      | false        | active   |
 * | present     | false        | disabled |
 * | present     | true         | hidden   |
 * | absent      | true         | hidden   |
 *
 * The two are read separately because they disagree in the middle case:
 * SportsPay issues the terminal the moment registration completes, while
 * `is_onboarded` only flips once the account is fully live. An org between
 * those points has already registered, so the button is shown but disabled —
 * visible progress, with no way to start a duplicate.
 *
 * `application_state` is deliberately NOT consulted; these two flags are the
 * source of truth.
 *
 * SCOPE: this gates the HEADER BUTTON only. The modal keeps its own onboarding
 * fetch, so its skeleton and status views stay reachable.
 */
export const resolveSportsPaySignupButtonState = (
  organization: SportsPayEligibleOrganization | null | undefined,
  permission?: SportsPayViewerPermission | null
): SportsPaySignupButtonState => {
  // Permissions arrive with the org payload, so an absent object means "not
  // known yet" — the same reason the org check below defaults to hidden.
  if (!permission?.admin) {
    return 'hidden'
  }
  // An absent org is "not known yet", not "eligible" — the payload is fetched,
  // and defaulting to visible would flash the button before it lands.
  if (!organization?.is_affiliate) {
    return 'hidden'
  }

  const sportspay = organization.sportspay
  // Live on SportsPay: nothing left to sign up for, whatever else is set.
  if (sportspay?.is_onboarded === true) {
    return 'hidden'
  }
  // Registered but not yet live. A blank value is not an issued terminal.
  if (sportspay?.terminal_id?.trim()) {
    return 'disabled'
  }
  return 'active'
}

/**
 * Whether to render the signup button at all — a thin wrapper over
 * `resolveSportsPaySignupButtonState` for callers that only need yes/no.
 */
export const shouldShowSportsPaySignupButton = (
  organization: SportsPayEligibleOrganization | null | undefined,
  permission?: SportsPayViewerPermission | null
): boolean =>
  resolveSportsPaySignupButtonState(organization, permission) !== 'hidden'
